import { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Box,
  InputAdornment,
  IconButton,
  Skeleton,
} from '@mui/material';
import { usePartyManager } from './PartyManagerContext';
import {
  POSSIBLE_SOURCES,
  EDIT_SOURCE,
  CODEMETA_SOURCE,
} from './partyManagerUtils';

import MailIcon from '@mui/icons-material/Mail';
import CallIcon from '@mui/icons-material/Call';
import FaxOutlinedIcon from '@mui/icons-material/FaxOutlined';
import LaunchIcon from '@mui/icons-material/Launch';

export const URL_PREFIX = 'https://';
const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
const isValidPhoneOrFax = (num) => /^\+?[0-9\s\-()]{7,}$/.test(String(num));
const isValidUrl = (url) => {
  if (!url) return false;
  try {
    const normalized = String(url).startsWith('http') ? url : `https://${url}`;
    const parsed = new URL(normalized);
    return !!parsed.hostname && !/\s/.test(parsed.hostname);
  } catch {
    return false;
  }
};

const isValidPrefixedUrl = (url) => {
  if (!url) return true;
  if (url === URL_PREFIX) return true;
  const domainPart = url.substring(URL_PREFIX.length);
  const hasDotInMiddle = /.+\..+/.test(domainPart) && !domainPart.endsWith('.');
  if (!hasDotInMiddle) {
    return false;
  }
  return isValidUrl(url);
};

const typeConfig = {
  email: {
    validation: isValidEmail,
    errorMessage: 'Ungültige E-Mail-Adresse',
    icon: <MailIcon />,
    inputType: 'email',
    onIconClick: (value) => {
      window.location.href = String(value).startsWith('mailto:')
        ? value
        : `mailto:${value}`;
    },
  },
  phone: {
    validation: isValidPhoneOrFax,
    errorMessage: 'Ungültige Telefonnummer',
    icon: <CallIcon />,
    inputType: 'tel',
    onIconClick: (value) => {
      window.location.href = String(value).startsWith('tel:')
        ? value
        : `tel:${value}`;
    },
  },
  fax: {
    validation: isValidPhoneOrFax,
    errorMessage: 'Ungültige Faxnummer',
    icon: <FaxOutlinedIcon />,
    inputType: 'tel',
    isIconDisabled: true,
  },
  website: {
    validation: isValidPrefixedUrl,
    errorMessage: 'Ungültige URL (Format: https://beispiel.de)',
    icon: <LaunchIcon />,
    inputType: 'url',
    onIconClick: (value) => {
      const hasProtocol =
        String(value).startsWith('http://') ||
        String(value).startsWith('https://');
      const urlToOpen = hasProtocol ? value : `${URL_PREFIX}${value}`;
      window.open(urlToOpen, '_blank');
    },
  },
};

const FormField = ({ fieldKey, label, type, isRequired }) => {
  const {
    isPartyManagerLoading,
    partyData,
    handleFieldChange,
    handleCycleSource,
    getAvailableSources,
  } = usePartyManager();

  const fieldState = partyData[fieldKey];
  if (!fieldState) return null;

  const displayValue =
    fieldState.selected === 'edit'
      ? fieldState.edit
      : fieldState[fieldState.selected];

  const [localValue, setLocalValue] = useState(displayValue || '');
  const [error, setError] = useState(false);
  const availableSources = getAvailableSources(fieldKey);
  const config = type ? typeConfig[type] : null;
  const isWebsite = type === 'website';

  useEffect(() => {
    let valueToSet = displayValue || '';
    if (isWebsite && valueToSet && !valueToSet.startsWith(URL_PREFIX)) {
      valueToSet = URL_PREFIX + valueToSet.replace(/^https?:\/\//, '');
    }
    if (isWebsite && displayValue === '') {
      valueToSet = '';
    }
    setLocalValue(valueToSet);
  }, [displayValue, isWebsite]);

  useEffect(() => {
    if (config && localValue) {
      const isValid = config.validation(localValue);
      setError(!isValid);
    } else {
      setError(false);
    }
  }, [localValue, config]);

  const handleBlur = () => {
    if (localValue === displayValue) {
      return;
    }

    const valueToSave =
      isWebsite && localValue === URL_PREFIX ? '' : localValue;

    const isPersonEmailField =
      partyData.partyType === 'PERSON' && fieldKey === 'email';
    let shouldTriggerSearch = false;
    if (isPersonEmailField) {
      const isEmailValid = typeConfig.email.validation(valueToSave);
      if (isEmailValid) {
        shouldTriggerSearch = true;
      }
    }

    handleFieldChange(fieldKey, valueToSave, shouldTriggerSearch);
  };

  const handleWebsiteChange = (e) => {
    const newValue = e.target.value;
    if (newValue === '') {
      setLocalValue('');
      return;
    }
    if (!localValue && newValue) {
      setLocalValue(URL_PREFIX + newValue);
      return;
    }
    if (!newValue.startsWith(URL_PREFIX)) {
      setLocalValue(localValue);
      return;
    }
    if (newValue === URL_PREFIX && localValue.length > URL_PREFIX.length) {
      setLocalValue('');
      return;
    }
    setLocalValue(newValue);
  };

  const getButtonIcon = () => {
    const selectedSource = fieldState.selected;
    if (selectedSource === 'edit') return EDIT_SOURCE.icon;
    if (selectedSource === 'codemeta') {
      return (
        <img
          src={CODEMETA_SOURCE.icon}
          alt={CODEMETA_SOURCE.name}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }
    const sourceInfo = POSSIBLE_SOURCES[selectedSource];
    if (sourceInfo?.icon) {
      return (
        <img
          src={sourceInfo.icon}
          alt={sourceInfo.name}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }
    return '?';
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleFieldChange(fieldKey, localValue, true);
    }
  };

  const inputProps = {};
  if (config) {
    inputProps.endAdornment = (
      <InputAdornment position="end">
        <IconButton
          onClick={() => config.onIconClick(localValue)}
          edge="end"
          disabled={
            !localValue ||
            error ||
            config.isIconDisabled ||
            (isWebsite && localValue === URL_PREFIX)
          }
          tabIndex={-1}
        >
          {config.icon}
        </IconButton>
      </InputAdornment>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        flexWrap: 'nowrap',
      }}
    >
      {isPartyManagerLoading && !isRequired ? (
        <Skeleton variant="rounded" width="100%" height={40} />
      ) : (
        <>
          <TextField
            label={
              <>
                {label}
                {isRequired && <span style={{ color: 'red' }}>*</span>}
              </>
            }
            size="small"
            value={localValue}
            onChange={
              isWebsite
                ? handleWebsiteChange
                : (e) => setLocalValue(e.target.value)
            }
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={
              isWebsite ? `${URL_PREFIX}codemeta.de` : `Wert für ${label}`
            }
            type={config?.inputType || 'text'}
            error={error}
            helperText={error ? config.errorMessage : ''}
            InputProps={inputProps}
            autoComplete="nope"
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiInputBase-input': {
                color: 'rgba(0, 0, 0, 0.87)',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(0, 0, 0, 0.38)',
                '&.MuiInputLabel-shrink': {
                  color: 'rgba(0, 0, 0, 0.87)',
                },
              },
            }}
          />
          {availableSources.length > 1 && (
            <Button
              title={`Quelle wechseln: Aktuell ${fieldState.selected}. Verfügbar: ${availableSources.join(', ')}`}
              sx={{
                width: '40px',
                height: '40px',
                minWidth: '40px',
                maxWidth: '40px',
                flexShrink: 0,
              }}
              onClick={() => handleCycleSource(fieldKey)}
              variant="outlined"
            >
              {getButtonIcon()}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default FormField;
